import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ITeam } from '@core/interfaces/team.interface';
import { IUser } from '@core/interfaces/user.interface';
import { TeamService } from '@core/services/team.service';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface ICampaignsDatatableState {
  users: IUser[];
  usersLoading: boolean;
  teams: ITeam[];
  teamsLoading: boolean;
}

const initialState: ICampaignsDatatableState = {
  teams: [],
  teamsLoading: false,
  users: [],
  usersLoading: false,
};

@Injectable()
export class CampaignsDatatableStore extends ComponentStore<ICampaignsDatatableState> {
  public readonly users$ = this.selectSignal((state: ICampaignsDatatableState) => state.users);
  public readonly usersLoading$ = this.selectSignal((state: ICampaignsDatatableState) => state.usersLoading);
  public readonly teams$ = this.selectSignal((state: ICampaignsDatatableState) => state.teams);
  public readonly teamsLoading$ = this.selectSignal((state: ICampaignsDatatableState) => state.teamsLoading);

  private readonly userService = inject(UserService);
  private readonly teamService = inject(TeamService);

  public readonly fetchUsers = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ usersLoading: true })),
      switchMap(() =>
        this.userService.getUsers(new HttpParams().set('limit', '1000')).pipe(
          tap((response: IApiResponse<IUser[]>) => {
            this.patchState({
              users: response.data ?? [],
              usersLoading: false,
            });
          }),
          catchError(() => {
            this.patchState({ users: [], usersLoading: false });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly fetchTeams = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ teamsLoading: true })),
      switchMap(() =>
        this.teamService.getTeams(new HttpParams().set('limit', '1000')).pipe(
          tap((response: IApiResponse<ITeam[]>) => {
            this.patchState({
              teams: response.data ?? [],
              teamsLoading: false,
            });
          }),
          catchError(() => {
            this.patchState({ teams: [], teamsLoading: false });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  constructor() {
    super(initialState);
  }
}
